<script setup lang="ts">
import { onMounted, computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Users, ClipboardCheck, TrendingUp } from 'lucide-vue-next';
import { useAuthStore } from '../stores/auth';
import { useFichesHeuresStore } from '../stores/fichesHeures';
import { useChantiersStore } from '../stores/chantiers';
import { usersApi, kpiApi, type TenantOverview } from '../api/endpoints';
import { useFormatters } from '../composables/useFormatters';
import { Card, Stat, Skeleton, RoleBadge } from '../components/ui';
import AppHeader from '../components/AppHeader.vue';

const router = useRouter();
const auth = useAuthStore();
const fichesHeuresStore = useFichesHeuresStore();
const chantiersStore = useChantiersStore();
const { t } = useI18n();
const { fmtPercent } = useFormatters();

// Compteur compagnons actifs du tenant (charge a la connexion via usersApi).
// On affiche le nombre dans la KPI "Compagnons" du dashboard.
const compagnonsCount = ref(0);

// KPI marge moyenne CONSOMMEE (chantiers actifs avec devis HT renseigne).
// Calculee cote backend via kpi.tenant_overview a partir des livraisons recues,
// des heures validees × taux horaire et du budget sous-traitance.
// Null si pas de donnees suffisantes.
const tenantKpi = ref<TenantOverview | null>(null);

// Hint contextuel sur la marge : compare consomme vs prevu pour signaler
// rapidement une derive. Si pas de donnees, message d'aide a la saisie.
const margeHint = computed(() => {
  if (!tenantKpi.value || tenantKpi.value.chantiers_count === 0) {
    return t('home.kpi.marge_hint_empty');
  }
  const prev = tenantKpi.value.marge_prevue_pct;
  const cons = tenantKpi.value.marge_consommee_pct;
  if (prev !== null && cons !== null) {
    const delta = cons - prev;
    if (Math.abs(delta) < 0.5) {
      return t('home.kpi.marge_hint_on_track', { count: tenantKpi.value.chantiers_count });
    }
    if (delta > 0) {
      return t('home.kpi.marge_hint_above', { delta: fmtPercent(Math.abs(delta), 1) });
    }
    return t('home.kpi.marge_hint_below', { delta: fmtPercent(Math.abs(delta), 1) });
  }
  return t('home.kpi.marge_hint', { count: tenantKpi.value.chantiers_count });
});

const chantiersActifsCount = computed(() => chantiersStore.activeList.length);

/**
 * Statut a valider selon le role connecte :
 *   - chef/conducteur : status=submitted (validation N1 a faire)
 *   - direction/admin : status=validated_chef (validation N2 a faire)
 *
 * Le compteur affiche dans la card "Fiches a valider" est le nombre de
 * fiches dans CE statut. La card est cliquable et navigue vers
 * /fiches-heures?status=<ce statut> + filtre semaine courante.
 */
const pendingStatus = computed<'submitted' | 'validated_chef'>(() => {
  const r = auth.user?.role;
  if (r === 'admin' || r === 'direction' || r === 'super_admin_sds') return 'validated_chef';
  return 'submitted';
});

const pendingCount = computed(() => {
  if (pendingStatus.value === 'validated_chef') return fichesHeuresStore.pendingDirectionCount;
  return fichesHeuresStore.pendingChefCount;
});

function goFichesAValider(): void {
  router.push({
    name: 'temps',
    query: { tab: 'fiches', status: pendingStatus.value },
  });
}

onMounted(async () => {
  if (!auth.user) {
    try {
      await auth.fetchMe();
    } catch {
      router.push({ name: 'login' });
      return;
    }
  }
  // Le compagnon n'a pas vocation a voir le dashboard admin (KPI marge,
  // fiches a valider, etc.). On le redirige vers /temps qui contient
  // sa page Pointage perso (tab Pointages) + ses fiches d'heures (tab Fiches).
  if (auth.user?.role === 'compagnon') {
    router.replace({ name: 'temps', query: { tab: 'pointages' } });
    return;
  }
  // Charge en parallele les 3 sources de KPI :
  //   - fiches a valider (compteur cliquable)
  //   - chantiers actifs (count via store)
  //   - compagnons actifs (count via users.list filtre role)
  // Erreurs silencieuses : la card affichera 0 plutot que tout le dashboard.
  // KPI marge n'est pertinent que pour le management (admin/direction/conducteur).
  // Le chef ne voit pas la marge tenant ; on saute l'appel pour eviter un 403 inutile.
  const wantsMarge = auth.user?.role === 'admin'
                  || auth.user?.role === 'direction'
                  || auth.user?.role === 'conducteur'
                  || auth.user?.role === 'super_admin_sds';

  await Promise.allSettled([
    fichesHeuresStore.fetchList({}),
    chantiersStore.fetchList(),
    usersApi.list('compagnon').then((res) => {
      compagnonsCount.value = (res.users || []).filter((u) => u.status === 'active').length;
    }),
    wantsMarge
      ? kpiApi.tenantOverview().then((data) => { tenantKpi.value = data; }).catch(() => { /* silent : la card affichera "—" */ })
      : Promise.resolve(),
  ]);
});

// Le chef voit 3 KPI utiles (chantiers actifs / compagnons / fiches a
// valider N1) mais PAS la marge tenant (confidentialite RH/finance).
const isChef = computed(() => auth.user?.role === 'chef');

// Roles qui voient les KPI strategiques. Le chef est inclus pour eviter
// un dashboard vide ; la marge est masquee separement plus bas.
const isManagement = computed(() => {
  const r = auth.user?.role;
  return r === 'admin' || r === 'direction' || r === 'conducteur'
      || r === 'chef' || r === 'super_admin_sds';
});
</script>

<template>
  <div class="min-h-screen bg-bg">
    <AppHeader />

    <main class="max-w-6xl mx-auto px-3 sm:px-6 py-10 flex flex-col gap-8">
      <section class="flex flex-col gap-2">
        <h1 class="text-2xl sm:text-4xl font-display font-semibold tracking-tight text-text leading-tight">
          <i18n-t keypath="home.welcome" tag="span">
            <template #name>
              <span class="text-accent">{{ auth.tenantName }}</span>
            </template>
          </i18n-t>
        </h1>
        <p class="text-md text-text-secondary max-w-prose">
          {{ t('home.subtitle') }}
        </p>
      </section>

      <!-- KPI admin/management : chantiers actifs / compagnons / fiches a valider / marge.
           Cache pour architecte / client_final (lecture seule restreinte).
           Les 3 premiers sont calcules dynamiquement depuis les stores. La marge est
           statique pour la demo (calcul real-time complexe et hors scope V2). -->
      <section v-if="isManagement" :class="['grid grid-cols-2 gap-4', isChef ? 'lg:grid-cols-3' : 'lg:grid-cols-4']">
        <Card>
          <Stat
            :label="t('home.kpi.chantiers_actifs')"
            :value="chantiersActifsCount"
            :hint="t('home.kpi.chantiers_hint')"
          />
        </Card>
        <Card>
          <Stat
            :label="t('home.kpi.compagnons_pointes')"
            :value="compagnonsCount"
            :hint="t('home.kpi.compagnons_hint')"
          />
        </Card>
        <Card
          class="cursor-pointer transition-transform hover:-translate-y-0.5 hover:shadow-md focus-within:ring-2 focus-within:ring-accent"
          role="button"
          tabindex="0"
          :aria-label="t('home.kpi.fiches_a_valider_aria')"
          @click="goFichesAValider"
          @keydown.enter="goFichesAValider"
        >
          <Stat
            :label="t('home.kpi.fiches_a_valider')"
            :value="pendingCount"
            :hint="t('home.kpi.fiches_a_valider_hint')"
          />
        </Card>
        <!-- Marge tenant : cachee au chef (confidentialite RH/finance).
             Calcul TEMPS REEL depuis kpi.tenant_overview :
               revenus  = sum(montant_devis_ht) des chantiers actifs
               couts    = livraisons recues + heures validees × taux horaire
                        + budget sous-traitance
               marge_consommee_pct = (revenus - couts) / revenus × 100
             Le hint montre l'ecart avec la marge prevue (utile pour detecter
             les chantiers en derive). "—" si donnees incompletes. -->
        <Card v-if="!isChef">
          <Stat
            :label="t('home.kpi.marge_moyenne')"
            :value="tenantKpi && tenantKpi.marge_consommee_pct !== null
              ? fmtPercent(tenantKpi.marge_consommee_pct, 1)
              : '—'"
            :hint="margeHint"
          />
        </Card>
      </section>

      <!-- Profil compact (nom + role) : utile pour reperer sous quel compte on est connecte.
           Le tenant_id technique et le badge "Phase X" ont ete supprimes : ces details
           internes trahissaient le mode dev pour un prospect. -->
      <section v-if="auth.user" class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <div class="flex items-center gap-4">
            <div class="h-12 w-12 rounded-full bg-accent/15 text-accent flex items-center justify-center text-lg font-semibold shrink-0">
              {{ auth.user.name.split(' ').map((p) => p.charAt(0).toUpperCase()).slice(0, 2).join('') }}
            </div>
            <div class="flex flex-col gap-1 min-w-0">
              <p class="font-medium text-text truncate">{{ auth.user.name }}</p>
              <RoleBadge :role="auth.user.role" />
            </div>
          </div>
        </Card>

        <!-- Activite recente : visible uniquement pour les roles management.
             Cache pour architecte et client_final qui n'ont rien a voir avec
             les pointages des compagnons ou les fiches d'heures internes. -->
        <Card v-if="isManagement" variant="subtle" class="lg:col-span-2">
          <div class="flex flex-col gap-4">
            <div class="flex items-center gap-3">
              <div class="h-9 w-9 rounded-md bg-accent/15 text-accent flex items-center justify-center">
                <TrendingUp :size="18" aria-hidden="true" />
              </div>
              <h3 class="text-md font-semibold font-display text-text">{{ t('home.activity.title') }}</h3>
            </div>
            <ul class="flex flex-col gap-3 text-sm">
              <li class="flex items-start justify-between gap-3">
                <div class="flex items-start gap-3 min-w-0">
                  <Users :size="14" class="mt-0.5 text-text-muted shrink-0" aria-hidden="true" />
                  <span class="text-text-secondary">
                    <i18n-t keypath="home.activity.clocked_in" tag="span">
                      <template #name><span class="font-medium text-text">Pedro Costa</span></template>
                      <template #chantier>Villa Cologny</template>
                    </i18n-t>
                  </span>
                </div>
                <span class="text-xs text-text-muted shrink-0">il y a 12 min</span>
              </li>
              <li class="flex items-start justify-between gap-3">
                <div class="flex items-start gap-3 min-w-0">
                  <ClipboardCheck :size="14" class="mt-0.5 text-text-muted shrink-0" aria-hidden="true" />
                  <span class="text-text-secondary">
                    <i18n-t keypath="home.activity.timesheet_submitted" tag="span">
                      <template #name><span class="font-medium text-text">Manuel Ferreira</span></template>
                    </i18n-t>
                  </span>
                </div>
                <span class="text-xs text-text-muted shrink-0">{{ t('home.activity.time_today') }}</span>
              </li>
            </ul>
          </div>
        </Card>
      </section>

      <div v-else>
        <Skeleton width="60%" height="1.25rem" />
      </div>
    </main>
  </div>
</template>
